import React, { useCallback, useEffect, useState } from "react";
import {
  Typography,
  Box,
  Button,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";

const fmtMoney = (n) =>
  `$${Number(n).toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const HubMedBulkBilledPricingPage = () => {
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newValue, setNewValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await phappsRequest(
        API_PATHS.HUBMED_ADMIN_BULK_BILLED_PRICES,
        "GET"
      );
      setPrices(Array.isArray(data?.prices) ? data.prices : []);
    } catch (err) {
      setError(err?.message ?? "Failed to load bulk-billed prices.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async () => {
    const value = Number(newValue);
    if (!Number.isFinite(value) || value <= 0) {
      setError("Enter a price greater than 0 (e.g. 51.25).");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await phappsRequest(API_PATHS.HUBMED_ADMIN_BULK_BILLED_PRICES, "POST", {
        price: Math.round(value * 100) / 100,
      });
      setNewValue("");
      await load();
    } catch (err) {
      setError(err?.message ?? "Failed to add price.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    setError(null);
    try {
      await phappsRequest(
        `${API_PATHS.HUBMED_ADMIN_BULK_BILLED_PRICES}/${id}`,
        "DELETE"
      );
      setPrices((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      setError(err?.message ?? "Failed to delete price.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 560 }}>
      <Typography variant="h5" fontWeight="bold" mb={1}>
        Bulk billed pricing list
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Invoice paid amounts that count as bulk billed (e.g. 51.25). On the
        appointment report, a paid amount that matches one of these values earns
        a $2.50 rebate; anything else earns 10% of the paid amount.
      </Typography>

      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", mb: 2 }}>
        <TextField
          size="small"
          label="Bulk billed price"
          placeholder="51.25"
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !saving) handleAdd();
          }}
          inputProps={{ inputMode: "decimal" }}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          sx={{ minWidth: 200 }}
        />
        <Button
          variant="contained"
          startIcon={
            saving ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <AddIcon />
            )
          }
          onClick={handleAdd}
          disabled={saving}
          sx={{ mt: 0.25 }}
        >
          Add
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : prices.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 2 }}>
          No bulk-billed prices yet. Add one above.
        </Typography>
      ) : (
        <List
          dense
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1.5,
          }}
        >
          {prices.map((p, i) => (
            <React.Fragment key={p.id}>
              {i > 0 && <Divider component="li" />}
              <ListItem
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => handleDelete(p.id)}
                    disabled={deletingId === p.id}
                  >
                    {deletingId === p.id ? (
                      <CircularProgress size={18} />
                    ) : (
                      <DeleteOutlineIcon />
                    )}
                  </IconButton>
                }
              >
                <ListItemText primary={fmtMoney(p.price)} />
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      )}
    </Box>
  );
};

export default HubMedBulkBilledPricingPage;
